import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { AIAssistant } from './components/AIAssistant';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ActsListPage from './pages/ActsListPage';
import ActDetailPage from './pages/ActDetailPage';
import CreateActPage from './pages/CreateActPage';
import AuditPage from './pages/AuditPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import AIEnginePage from './pages/AIEnginePage';
import AlertsPage from './pages/AlertsPage';
import AdminGovernoPage from './pages/AdminGovernoPage';
import AdminBlueGovPage from './pages/AdminBlueGovPage';
import ContratoPage from './pages/ContratoPage';
import BlueGovLoginPage from './pages/BlueGovLoginPage';
import BlueGovDashboardPage from './pages/BlueGovDashboardPage';
import CasaCivilDashboard from './pages/CasaCivilDashboard';
import CGEDashboard from './pages/CGEDashboard';
import TokenIntelligencePage from './pages/TokenIntelligencePage';
import IntegrationsPage from './pages/IntegrationsPage';
import TCEPage from './pages/TCEPage';
import IntegrityProgramPage from './pages/IntegrityProgramPage';
// Novos módulos - Blocos 2, 3, 4, 6
import LGPDPage from './pages/LGPDPage';
import GovernancePage from './pages/GovernancePage';
import TransparencyPage from './pages/TransparencyPage';
import ReportsPage from './pages/ReportsPage';
import InboxPage from './pages/InboxPage';
import NotificationsPage from './pages/NotificationsPage';
import HealthPage from './pages/HealthPage';
import HelpPage from './pages/HelpPage';
import ExecutiveViewPage from './pages/ExecutiveViewPage';
import VerifyDossierPage from './pages/VerifyDossierPage';
import AuditorExternoLoginPage from './pages/AuditorExternoLoginPage';
import AzureSuccessPage from './pages/AzureSuccessPage';
import LegalPage from './pages/LegalPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1d4ed8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs onboarding
  if (user?.tenant_id === 'default' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1d4ed8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.tenant_id === 'default') {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Rotas públicas — verificação de dossiê e acesso auditor externo */}
      <Route path="/verify-dossier/:dossierId" element={<VerifyDossierPage />} />
      <Route path="/auditor" element={<AuditorExternoLoginPage />} />
      <Route path="/auth/azure-success" element={<AzureSuccessPage />} />
      <Route path="/termos-de-uso" element={<LegalPage />} />
      <Route path="/politica-de-privacidade" element={<LegalPage />} />
      <Route path="/lgpd" element={<LegalPage />} />
      <Route path="/cookies" element={<LegalPage />} />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive"
        element={
          <ProtectedRoute>
            <ExecutiveViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acts"
        element={
          <ProtectedRoute>
            <ActsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <InboxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <HealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acts/new"
        element={
          <ProtectedRoute>
            <CreateActPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acts/:actId"
        element={
          <ProtectedRoute>
            <ActDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-engine"
        element={
          <ProtectedRoute>
            <AIEnginePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-engine/:actId"
        element={
          <ProtectedRoute>
            <AIEnginePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/casa-civil"
        element={
          <ProtectedRoute>
            <CasaCivilDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/cge"
        element={
          <ProtectedRoute>
            <CGEDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminGovernoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/governo"
        element={
          <ProtectedRoute>
            <AdminGovernoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bluegov"
        element={
          <ProtectedRoute>
            <AdminBlueGovPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tce"
        element={
          <ProtectedRoute>
            <TCEPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integridade"
        element={
          <ProtectedRoute>
            <IntegrityProgramPage />
          </ProtectedRoute>
        }
      />
      {/* Bloco 2 - LGPD (interno, dentro do AppLayout) */}
      <Route
        path="/compliance/lgpd"
        element={
          <ProtectedRoute>
            <LGPDPage />
          </ProtectedRoute>
        }
      />
      {/* Bloco 3 - Governança e PDTIC */}
      <Route
        path="/governanca"
        element={
          <ProtectedRoute>
            <GovernancePage />
          </ProtectedRoute>
        }
      />
      {/* Bloco 4 - Portal de Transparência (público, mas dentro do layout) */}
      <Route
        path="/transparencia"
        element={
          <ProtectedRoute>
            <TransparencyPage />
          </ProtectedRoute>
        }
      />
      {/* Bloco 6 - Relatórios de Edital */}
      <Route
        path="/relatorios"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/contrato" element={<ContratoPage />} />

      {/* BlueGov Admin (Fornecedor SaaS) - Rotas Separadas */}
      <Route path="/bluegov/login" element={<BlueGovLoginPage />} />
      <Route path="/bluegov/dashboard" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/clients" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/contracts" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/licenses" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/metrics" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/tickets" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/renewals" element={<BlueGovDashboardPage />} />
      <Route path="/bluegov/token-intelligence" element={<TokenIntelligencePage />} />
      <Route path="/bluegov" element={<BlueGovLoginPage />} />

      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-slate-900">404</h1>
              <p className="text-slate-500 mt-2">Página não encontrada</p>
              <a href="/dashboard" className="text-[#1d4ed8] hover:underline mt-4 inline-block">
                Voltar ao Dashboard
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <AIAssistant />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
